/**
 * Resolve the URL of the full agent app's React frontend (NOT the API).
 * The "Open in Agent" ↗ button in the chat-shell header uses this to
 * deep-link the user into their conversation on agent.ordo.com.
 *
 * Resolution order:
 *  1. Explicit override from the host's `AgentEmbedConfig.agentAppUrl`
 *     — preferred; covers staging, preview tunnels, etc.
 *  2. Localhost detection — when the embed is running on `localhost`
 *     or `127.0.0.1`, default to `http://localhost:3020`, which is
 *     the agent Vite client port pinned in `scripts/dev-embed.sh`.
 *  3. Production default — `https://agent.ordo.com`.
 *
 * Returned URL has no trailing slash. Callers append `/chat/<id>` as
 * needed via `buildAgentChatUrl`.
 */
export function resolveAgentAppUrl(override?: string): string {
  if (override && override.length > 0) {
    return override.replace(/\/$/, '')
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3020'
    }
  }
  return 'https://agent.ordo.com'
}

/**
 * Build a deep-link to a specific conversation in the agent app.
 * When `conversationId` is missing, returns the base URL — useful
 * for "open agent home" affordances. Path matches the agent app's
 * router (`/chat/:id` in `apps/agent/client/src/routes/index.tsx`).
 */
export function buildAgentChatUrl(conversationId: string | null | undefined, override?: string): string {
  const base = resolveAgentAppUrl(override)
  return conversationId ? `${base}/chat/${conversationId}` : base
}
